import React, { FunctionComponent, useEffect, useState } from 'react';
import { descend, map, mapObjIndexed, prop, sort } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import { columns } from './utils';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import Icon from '../../components/Icon';
import Input from '../../components/Input';
import RegularButton from '../../components/RegularButton';
import Table from '../../components/Table';
import { IAppState } from '../../common/models/store/IAppState';
import { setContactInfoErrorRoutine, mergeContactsRoutine } from './routines';
import { usePrevious } from '../../common/helpers/customHooks';
import LoaderWrapper from '../../components/LoaderWrapper';
import ConfirmationModal from '../../components/ConfirmationModal';
import { IMergeContact } from '../../common/models/contact/IMergeContact';
import { IMergedContactInfo } from '../../common/models/contact/IMergedContactInfo';
import { reqBuilderStep2ValidationSchema } from '../../common/validation/yup';

interface IProps {
  contacts: IMergedContactInfo[];
  contactInfoError: boolean;
  isSaving: boolean;
  error: string | null;
  setError: IBindingCallback1<boolean>;
  mergeContacts: IBindingCallback1<IMergeContact>;
  onHide: IBindingAction;
}

const MergeContacts: FunctionComponent<IProps> = ({
  error,
  isSaving,
  contacts,
  contactInfoError,
  onHide,
  setError,
  mergeContacts
}) => {
  const sortedContacts = sort(descend(prop('lastModifiedDate')), contacts);
  const [lastUsedContact] = sortedContacts;
  const initialValues = {
    firstName: lastUsedContact.firstName,
    lastName: lastUsedContact.lastName,
    email: lastUsedContact.email || '',
    phoneNumber: lastUsedContact.phoneNumber || ''
  };
  type FormValues = typeof initialValues;
  const ids = map(({ id }) => id, sortedContacts);
  const [isConfirmShown, setIsConfirmShown] = useState(false);

  const { values, errors, isValid, setFieldValue, submitForm } = useFormik<FormValues>({
    initialValues,
    onSubmit: () => setIsConfirmShown(true),
    validateOnMount: true,
    validationSchema: reqBuilderStep2ValidationSchema
  });

  const onMerge = () => {
    setIsConfirmShown(false);
    const { email, phoneNumber, ...rest } = mapObjIndexed(val => val.trim(), values);
    mergeContacts({
      ids,
      ...rest,
      email: email || null,
      phoneNumber: phoneNumber || null
    });
  };

  const prevIsSaving = usePrevious(isSaving);
  useEffect(() => {
    if (prevIsSaving && !isSaving && !error && !contactInfoError) {
      onHide();
    }
  }, [isSaving]);

  useEffect(() => {
    if (contactInfoError) {
      setError(false);
    }
  }, [values.email.trim(), values.phoneNumber]);

  useEffect(() => () => setError(false), []);

  return (
    <LoaderWrapper loading={isSaving} transparent>
      <ConfirmationModal
        isShown={isConfirmShown}
        titleText={{ id: 'merge.confirmTitle', defaultMessage: 'Are you sure you want to merge?' }}
        descriptionText={{
          id: 'merge.confirmDescr',
          defaultMessage: 'Merging cannot be undone. Are you sure you want to merge these?'
        }}
        confirmBttnText={{ id: 'merge.confirmBttn', defaultMessage: 'Yes, merge' }}
        cancelBttnText={{ id: 'goBack', defaultMessage: 'No, go back' }}
        onCancel={() => setIsConfirmShown(false)}
        onConfirm={onMerge}
      />
      <div className="merge-modal">
        <header className="custom-modal-header">
          <FormattedMessage id="mergeContacts.title" defaultMessage="Merge contacts" />
        </header>
        <main className="flex-grow-1 d-flex flex-column">
          <div className="merge-info">
            <div className="contact-title">
              <FormattedMessage
                id="mergeContacts.description"
                defaultMessage="What should the merged contact’s information be?"
              />
            </div>
            <div className="contact-form-container">
              <Input
                value={values.email}
                onChange={val => setFieldValue('email', val)}
                label={{ id: 'email', defaultMessage: 'Email' }}
                variant="underlined"
                errorIntl={contactInfoError
                  ? {
                    id: 'contactProfile.candidate.editError',
                    defaultMessage: 'Contact with such data already exists'
                  }
                  : errors.email}
              />
              <Input
                value={values.firstName}
                onChange={val => setFieldValue('firstName', val)}
                label={{ id: 'firstName', defaultMessage: 'First name' }}
                variant="underlined"
                errorIntl={errors.firstName}
              />
              <Input
                value={values.lastName}
                onChange={val => setFieldValue('lastName', val)}
                label={{ id: 'lastName', defaultMessage: 'Last name' }}
                variant="underlined"
                errorIntl={errors.lastName}
              />
              <Input
                value={values.phoneNumber}
                onChange={val => setFieldValue('phoneNumber', val)}
                label={{ id: 'phoneNumber', defaultMessage: 'Phone number' }}
                variant="underlined"
                type="phoneNumber"
                errorIntl={contactInfoError
                  ? {
                    id: 'contactProfile.candidate.editError',
                    defaultMessage: 'Contact with such data already exists'
                  }
                  : errors.phoneNumber}
              />
            </div>
          </div>
          <div className="merge-selected">
            <div className="merge-selected-title">
              <FormattedMessage id="mergeContacts.selected" defaultMessage="Selected contacts" />
              <div className="number-badge">{contacts.length}</div>
            </div>
            <Table
              config={{
                data: { items: sortedContacts, total: contacts.length },
                columns
              }}
            />
          </div>
        </main>
        <footer className="modal-footer-bttns">
          <RegularButton
            textIntlId="cancel"
            defaultMessage="Cancel"
            onClick={onHide}
            variant="tertiary"
          />
          <RegularButton
            textIntlId="iconButtonLable.merge"
            defaultMessage="Merge"
            icon={<Icon icon="merge" />}
            disabled={!isValid || contactInfoError || isSaving}
            onClick={submitForm}
          />
        </footer>
      </div>
    </LoaderWrapper>
  );
};

const mapStateToProps = ({
  mergeContacts: { contactInfoError, isSaving, error }
}: IAppState) => ({ contactInfoError, isSaving, error });

const mapDispatchToProps = {
  mergeContacts: mergeContactsRoutine,
  setError: setContactInfoErrorRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(MergeContacts);
